import React, { useState } from 'react';
import { useNotify } from 'react-admin';
import { useQueryClient } from 'react-query';
import { Check, Download, FileText, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiFetch, apiFetchResponse } from '../../lib/apiFetch';
import { EmailAttachment, fileTypeLabel, formatBytes } from '../emailThreads';

/**
 * Case-document types an email attachment can be filed as. Mirrors the
 * backend's SAVEABLE_DOCUMENT_TYPES (email-attachments.rules.ts): the upload
 * slots in documentSlots.ts plus the T-108 extra types.
 */
export const SAVE_AS_TYPES: Array<{ value: string; label: string }> = [
  { value: 'medical_certificate', label: 'Medical certificate (MCCD)' },
  { value: 'sp3', label: 'SP-3' },
  { value: 'coroner_certificate', label: "Coroner's certificate" },
  { value: 'coroner_release', label: "Coroner's warrant / release" },
  { value: 'burial_permit_signed', label: 'Burial permit (signed)' },
  { value: 'preneed_confirmation', label: 'Preneed confirmation' },
  { value: 'identification', label: 'ID / Proof of identity' },
  { value: 'belongings_list', label: 'Belongings list' },
  { value: 'other', label: 'Other' },
];

/** Why a file isn't downloadable, in a word or two. */
const SKIP_WORD: Record<string, string> = {
  too_large: 'Too large',
  blocked_type: 'Blocked',
  empty: 'Empty',
  upload_failed: 'Not saved',
};

interface Props {
  attachments: EmailAttachment[];
  /** Absent on a lead: there is no case to save to, so Download only. */
  funeralId?: number;
  /** Older emails: metadata only, the bytes were never kept. */
  stored: boolean;
  /** Extra attachments counted in metadata but missing from the list. */
  unlistedCount: number;
  gmailAvailable: boolean;
}

/** One row per attachment: type, name, size, Download, Save to case. */
export function EmailAttachmentList({ attachments, funeralId, stored, unlistedCount, gmailAvailable }: Props) {
  return (
    <div className="space-y-1.5" data-email-attachments>
      {attachments.length > 0 && (
        <ul className="divide-y rounded-md border bg-background">
          {attachments.map((a, i) => (
            <AttachmentRow key={a.id ?? `${a.filename}-${i}`} attachment={a} funeralId={funeralId} />
          ))}
        </ul>
      )}
      {!stored && (
        <p className="text-[11px] text-muted-foreground">
          {unlistedCount > 0 ? `${unlistedCount} attachment${unlistedCount > 1 ? 's' : ''}. ` : ''}
          This email arrived before files were kept{gmailAvailable ? '; open in Gmail to download' : ''}.
        </p>
      )}
    </div>
  );
}

function AttachmentRow({ attachment: a, funeralId }: { attachment: EmailAttachment; funeralId?: number }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [downloading, setDownloading] = useState(false);
  const [picking, setPicking] = useState(false);
  const [docType, setDocType] = useState('');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAs, setSavedAs] = useState<string | null>(a.funeral_document_id ? 'saved' : null);

  const downloadable = !!a.id && !!a.stored;
  const skipWord = a.id && !a.stored ? SKIP_WORD[a.skip_reason || ''] || 'Not saved' : null;

  const download = async () => {
    if (!a.id) return;
    setDownloading(true);
    try {
      // A 5-minute signed URL from the private bucket; it downloads under the original name.
      const { url } = (await apiFetch(`email-attachments/${a.id}/url`)) as { url: string };
      // An anchor click, not window.open: after an await, popup blockers
      // would eat a new window, but a download is allowed.
      const link = document.createElement('a');
      link.href = url;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Attachment download failed:', err);
      notify('Could not download this file', { type: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const save = async () => {
    if (!a.id || !docType) return;
    setSaving(true);
    try {
      const res = await apiFetchResponse(`email-attachments/${a.id}/save-to-documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          funeral_id: funeralId,
          document_type: docType,
          ...(docType === 'other' && label.trim() ? { label: label.trim() } : {}),
        }),
      });
      if (res.status === 422) {
        notify('This PDF is password-protected. Download it and upload it from the Documents tab with the password.', {
          type: 'warning',
        });
        return;
      }
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const typeLabel = SAVE_AS_TYPES.find((t) => t.value === docType)?.label ?? docType;
      setSavedAs(typeLabel);
      setPicking(false);
      notify(`Saved to case documents as ${typeLabel}`, { type: 'success' });
      void queryClient.invalidateQueries(['funeral_feed_email_headers']);
    } catch (err) {
      console.error('Save to documents failed:', err);
      notify('Could not save this file to the case', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="px-2 py-1.5">
      <div className="flex items-center gap-2 min-w-0">
        <span className="grid h-7 w-8 shrink-0 place-items-center rounded bg-muted font-mono text-[10px] font-medium">
          {fileTypeLabel(a)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-medium" title={a.filename}>
            {a.filename || 'attachment'}
          </span>
          <span className="block text-[11px] text-muted-foreground tabular-nums">
            {formatBytes(a.size_bytes) || ' '}
          </span>
        </span>
        {skipWord && (
          <span
            className="shrink-0 text-[11px] text-muted-foreground"
            title={a.skip_reason === 'blocked_type' ? 'Executable files are never kept' : undefined}
          >
            {skipWord}
          </span>
        )}
        {savedAs && (
          <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground" title={savedAs}>
            <Check className="h-3 w-3" /> In documents
          </span>
        )}
        {a.funeral_document_type === 'trustage_certificate' && (
          <span className="inline-flex shrink-0 items-center rounded-md bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-800">
            Filed as TruStage certificate
          </span>
        )}
        {downloadable && (
          <>
            <button
              type="button"
              onClick={() => void download()}
              disabled={downloading}
              className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
              title="Download"
            >
              {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              <span className="max-sm:hidden">Download</span>
            </button>
            {!savedAs && funeralId != null && (
              <button
                type="button"
                onClick={() => setPicking((p) => !p)}
                aria-expanded={picking}
                className={cn(
                  'inline-flex h-7 shrink-0 items-center gap-1 rounded-md border px-2 text-xs font-medium hover:bg-muted',
                  picking && 'bg-muted',
                )}
              >
                <FileText className="h-3.5 w-3.5" />
                Save to case
              </button>
            )}
          </>
        )}
      </div>

      {picking && (
        <div className="mt-1.5 flex flex-wrap items-center gap-2 pl-10">
          <select
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            className="h-8 rounded-md border bg-background px-2 text-xs"
            aria-label="Document type"
            autoFocus
          >
            <option value="">Document type…</option>
            {SAVE_AS_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          {docType === 'other' && (
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Name (e.g. Hospital release)"
              className="h-8 min-w-0 flex-1 rounded-md border bg-background px-2 text-xs"
              aria-label="Document name"
            />
          )}
          <button
            type="button"
            onClick={() => void save()}
            disabled={!docType || saving}
            className="inline-flex h-8 items-center gap-1 rounded-md border border-primary bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save
          </button>
        </div>
      )}
    </li>
  );
}
